import React from "react";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";

function About() {
    const currentYear = new Date().getFullYear();

    const policyItems = [
        {
            label: "Terms & Conditions",
            url: APP_THEME.policyUrls?.terms || "#",
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                </svg>
            ),
            description: "Read our terms of service"
        },
        {
            label: "Privacy Policy",
            url: APP_THEME.policyUrls?.privacy || "#",
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
            ),
            description: "How we protect your data"
        },
        {
            label: "Refund Policy",
            url: APP_THEME.policyUrls?.refund || "#",
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-9-9" />
                    <polyline points="15 6 21 12 15 18" />
                    <line x1="12" y1="12" x2="21" y2="12" />
                </svg>
            ),
            description: "Get your money back"
        },
        {
            label: "Cancellation Policy",
            url: APP_THEME.policyUrls?.cancellation || "#",
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="18" y1="6" x2="6" y2="18" />
                </svg>
            ),
            description: "Cancel your transactions"
        },
        {
            label: "Account Deletion",
            url: APP_THEME.policyUrls?.accountDeletion || "#",
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <line x1="17" y1="9" x2="22" y2="14" />
                    <line x1="22" y1="9" x2="17" y2="14" />
                </svg>
            ),
            description: "Request account deletion"
        },
    ];

    return (
        <>
            <Header title={`About ${APP_THEME.appName}`} showBack={true} />
            <div className="flex flex-col min-h-[calc(100vh-57px)]">
                {/* App Info Section */}
                <div className="flex-1 px-4 py-6">
                    {/* App Logo */}
                    <div className="flex justify-center mb-4">
                        <img
                            src="/logo512.png"
                            alt={APP_THEME.appName || "App Logo"}
                            className="w-20 h-20 rounded-2xl shadow-lg object-contain"
                        />
                    </div>

                    {/* App Name */}
                    <h1
                        className="text-xl font-bold text-center"
                        style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                    >
                        {APP_THEME.appName}
                    </h1>

                    <p className="text-md mb-1 text-center" style={{ color: APP_THEME.textSecondary }}>
                        {APP_THEME.slogan}
                    </p>

                    {/* Version */}
                    <p
                        className="text-xs text-center mb-6"
                        style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
                    >
                        Web Version {APP_THEME.appVersion}
                    </p>

                    {/* Business Name */}
                    <div
                        className="rounded-2xl p-4 mb-4 border"
                        style={{
                            background: APP_THEME.surfaceColor,
                            borderColor: APP_THEME.borderColor,
                        }}
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.primaryColor} strokeWidth="2">
                                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                                <line x1="16" y1="21" x2="16" y2="15" />
                                <line x1="8" y1="21" x2="8" y2="15" />
                                <line x1="9" y1="3" x2="15" y2="3" />
                            </svg>
                            <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary }}>
                                Company
                            </span>
                        </div>
                        <p className="text-sm font-medium mb-1" style={{ color: APP_THEME.textPrimary }}>
                            {APP_THEME.businessName}
                        </p>
                        {APP_THEME.businessAddress && (
                            <p className="text-xs" style={{ color: APP_THEME.textSecondary }}>
                                {APP_THEME.businessAddress}
                            </p>
                        )}
                    </div>

                    {/* Policies Section */}
                    <div
                        className="rounded-2xl border overflow-hidden"
                        style={{
                            background: APP_THEME.surfaceColor,
                            borderColor: APP_THEME.borderColor,
                        }}
                    >
                        <div className="p-4 border-b" style={{ borderColor: APP_THEME.borderColor }}>
                            <div className="flex items-center gap-2">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.primaryColor} strokeWidth="2">
                                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                                    <path d="M2 17l10 5 10-5" />
                                    <path d="M2 12l10 5 10-5" />
                                </svg>
                                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary }}>
                                    Policies & Legal
                                </span>
                            </div>
                        </div>
                        {policyItems.map((item, index) => (
                            <a
                                key={item.label}
                                href={item.url} target="_blank" rel="noopener noreferrer"
                                className={`flex items-center justify-between p-4 cursor-pointer transition-all duration-200 active:scale-98 ${index < policyItems.length - 1 ? "border-b" : ""
                                    }`}
                                style={{ borderColor: APP_THEME.borderColor }}
                            >
                                <div className="flex items-center gap-3 flex-1">
                                    <div style={{ color: APP_THEME.primaryColor }}>
                                        {item.icon}
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary }}>
                                            {item.label}
                                        </div>
                                        <div className="text-[10px] mt-0.5" style={{ color: APP_THEME.textSecondary }}>
                                            {item.description}
                                        </div>
                                    </div>
                                </div>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="2">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    <polyline points="15 3 21 3 21 9" />
                                    <line x1="10" y1="14" x2="21" y2="3" />
                                </svg>
                            </a>
                        ))}
                    </div>

                    {/* Account Deletion Note */}
                    <div
                        className="rounded-2xl p-4 mt-4 border"
                        style={{
                            background: `${APP_THEME.errorColor}08`,
                            borderColor: `${APP_THEME.errorColor}20`,
                        }}
                    >
                        <div className="flex items-start gap-2">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.errorColor} strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <div className="flex-1">
                                <p className="text-xs" style={{ color: APP_THEME.textSecondary }}>
                                    For account deletion requests, please contact our support team at{' '}
                                    <strong style={{ color: APP_THEME.textPrimary }}>
                                        {APP_THEME.support.emailSupport}
                                    </strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Info */}
                <div className="px-4 pb-6 pt-2">
                    <div
                        className="h-px w-full mb-4"
                        style={{ background: APP_THEME.borderColor }}
                    />
                    <div className="text-center">
                        <p
                            className="text-[11px]"
                            style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
                        >
                            © {currentYear} {APP_THEME.businessName}. All rights reserved.
                        </p>
                        <p
                            className="text-[10px] mt-1"
                            style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
                        >
                            CIN: {APP_THEME.cin}
                        </p>
                    </div>
                </div>
            </div>

            <style>
                {`
                    .active\\:scale-98:active {
                        transform: scale(0.98);
                    }
                `}
            </style>
        </>
    );
}

export default About;